import { useMemo } from 'react';
import { useGetClassesQuery } from '../../store/api/localApi';
import type { PolicyAudience, PolicyCoverage } from '../../types';

/** The default audience, and what an absent stored audience means. */
export const EVERYONE_AT_LOCATION: PolicyAudience = { scope: 'LOCATION' };
const EVERYONE_AT_LOCATION_LABEL = 'Everyone at this location';
export const CLASS_REMOVED = '(class removed)';
/** Shown when the server refuses an audience naming a class from another location. */
export const CLASS_NOT_AT_LOCATION_MESSAGE =
  'One of those classes is not at this location. Refresh the page and pick again.';

/**
 * Every class at the location, active or closed, keyed by id. A closed class a
 * policy is still sent to keeps its name; only an id with no class at all reads
 * "(class removed)". `undefined` while the classes are still loading, so
 * callers do not mislabel a real class as removed for a moment.
 */
export function useLocationClassNames(
  locationId: string,
  skip = false
): ReadonlyMap<string, string> | undefined {
  const { data } = useGetClassesQuery(locationId, { skip: skip || !locationId });
  return useMemo(() => (data ? new Map(data.map((c) => [c.id, c.name])) : undefined), [data]);
}

/** "Everyone at this location", or the chosen classes' names joined by ", ". */
export function audienceLabel(
  audience: PolicyAudience | undefined,
  classNames: ReadonlyMap<string, string> | undefined
): string {
  if (!audience || audience.scope === 'LOCATION') return EVERYONE_AT_LOCATION_LABEL;
  return audience.classIds
    .map((id) => (classNames ? classNames.get(id) ?? CLASS_REMOVED : '…'))
    .join(', ');
}

/** The same label from coverage, which names the classes itself. Null for an older backend. */
export function coverageAudienceLabel(audience: PolicyCoverage['audience']): string | null {
  if (!audience) return null;
  if (audience.scope === 'LOCATION') return EVERYONE_AT_LOCATION_LABEL;
  return audience.classes.map((c) => (c.removed ? CLASS_REMOVED : c.name)).join(', ');
}
